import { useCallback } from 'react';
import { useUpdateDatasetData } from '../../../../../../api/hooks/useUpdateDatasetData';
import {
  Dataset,
  DatasetItem,
  GridRow,
} from '../../../../../../types/types';

type DatasetRow = Record<string, unknown>;

type InlineDatasetEditResult = {
  save: (
    row: DatasetRow,
    column: DatasetItem,
    value: number
  ) => Promise<boolean>;
};

type InlineDatasetEditCallbacks = {
  onRowPatch: (id: string, patch: DatasetRow) => void;
  onMasterPatch?: (patch: GridRow) => void;
};

export const useInlineDatasetEdit = (
  dataset: Dataset | undefined,
  { onRowPatch, onMasterPatch }: InlineDatasetEditCallbacks
): InlineDatasetEditResult => {
  const { update } = useUpdateDatasetData();
  const idItem = dataset?.idItem ?? '_id';

  const save = useCallback(
    async (
      row: DatasetRow,
      column: DatasetItem,
      value: number
    ): Promise<boolean> => {
      const id = String(row[idItem] ?? '');
      const apiMethod = column.inlineEdit?.apiMethod;
      if (!id || !apiMethod) return false;

      const result = await update(`${apiMethod}/${encodeURIComponent(id)}`, {
        id,
        [column.name]: value,
      });
      if (result?.error) return false;

      const responseRow = Array.isArray(result?.response?.data)
        ? result.response.data[0]
        : undefined;
      const rowPatch = { [column.name]: value, ...(responseRow ?? {}) };
      onRowPatch(id, rowPatch);
      const masterPatch = Array.isArray(result?.response?.masterData)
        ? result.response.masterData[0]
        : undefined;
      if (masterPatch) onMasterPatch?.(masterPatch);
      return true;
    },
    [idItem, onMasterPatch, onRowPatch, update]
  );

  return { save };
};
